/**
 * Hook for generating pricing snapshots for offers and comparisons
 * Replaces the legacy calculateTabPricing function with bundle API integration
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { formatVoiceTrafficLabel } from '../utils/voiceTrafficUtils';
import { useCalculator } from '../contexts/CalculatorContext';
import { usePricing } from '../contexts/PricingContext';
import { calculateSubscriptionPlans } from '../utils/subscriptionCalculator';
import { convertUSDToSubscriptionAmount } from '../utils/currencyUtils';
import { sortProductsByPriority } from '../utils/snapshotUtils';
import {
  normalizePricingItemForRegion,
  normalizePhoneLineForTotal,
} from '../utils/pricingItemNormalizer';
import { buildPhoneNumberPricingItems } from '../utils/phoneNumberPricingItems';
import {
  buildActiveTabSnapshot,
  buildOneTimePayments,
  SUBSCRIPTION_ELIGIBLE_PRODUCT_TYPES,
} from './pricingSnapshotBuilder';
import { getEffectiveChatUsers, getEffectiveTierUsers, getMinUsersForTier } from '../utils/tierUsers';
import {
  ENIQ_MIN_BILLABLE_USERS,
  getEniqTierDisplayName,
  isEniqIncludedInTier,
  normalizeEniqTier,
} from '../utils/eniqPricing';
import { isEniqPerUserMode } from '../config/pricingModes';

// Helper function to get currency symbol for region
const getCurrencySymbolForRegion = (region) => {
  switch (region) {
    case 'ua': return '$'; // Ukraine uses USD
    case 'eu': return '€'; // Europe uses EUR
    default: return '$'; // Asia and Other use USD
  }
};

// Helper function to get plan name (keep in English on all languages)
const getPlanName = (planId, t) => {
  switch (planId) {
    case 'start': return 'Start';
    case 'lite': return 'Lite';
    case 'pro': return 'Pro';
    case 'max': return 'Max';
    case 'scale': return 'Scale';
    default: return planId || 'Lite';
  }
};

/**
 * Generate pricing snapshot for tab data
 * @param {Object} tabData - Tab configuration data
 * @param {boolean} isActiveTab - Whether this is the currently active tab
 * @returns {Object} Pricing snapshot formatted for offers/comparisons
 */
export const usePricingSnapshot = () => {
  const { t } = useTranslation();
  const { calculateBundle, currencyRates } = usePricing();
  
  // For active tab - reuse live pricing from CalculatorContext
  const {
    summaryItemsForSubscription,
    summaryItems,
    total,
    currency,
    region,
    customDiscount360,
    customDiscount180,
    phoneNumbers,
    enablePhoneActivation,
    eniqAiImplementationFee,
    enableEniqImplementationFee,
    voicebotImplementationFee,
    enableVoicebotImplementationFee,
    isProductSelected
  } = useCalculator();

  const generateSnapshotForActiveTab = useCallback((tabData) => {
    const { oneTimePayments, totalOneTimePayments } = buildOneTimePayments({
      t,
      region,
      currencyRates,
      phoneNumbers,
      enablePhoneActivation,
      isProductSelected,
      eniqAiImplementationFee,
      enableEniqImplementationFee,
      voicebotImplementationFee,
      enableVoicebotImplementationFee,
    });

    // Гілка skyline-30-08-2026 додала сюди лише `eniqTier: item.eniqTier` у
    // individual_prices — builder покриває це через `...item` spread, тож
    // окремого перенесення не потрібно.
    return buildActiveTabSnapshot({
      t,
      tabData,
      summaryItemsForSubscription,
      summaryItems,
      total,
      currency,
      region,
      customDiscount360,
      customDiscount180,
      currencyRates,
      oneTimePayments,
      totalOneTimePayments,
    });
  }, [summaryItemsForSubscription, summaryItems, total, currency, region, t, customDiscount360, customDiscount180, phoneNumbers, enablePhoneActivation, eniqAiImplementationFee, enableEniqImplementationFee, voicebotImplementationFee, enableVoicebotImplementationFee, isProductSelected, currencyRates]);

  const generateSnapshotForInactiveTab = useCallback(async (tabData) => {

    const settings = tabData.settings || {};
    const products = settings.products || {};
    const selectedProducts = products.selectedProducts || [];

    // Read packageTier for proper pricing calculation
    const packageTier = settings.packageTier || 'basic';

    let totalUSD = 0;
    const baseSummaryItems = []; // Base prices for subscription calculator
    const finalSummaryItems = []; // Final prices with bundle discounts for display
    const bundleProducts = []; // Products for bundle API
    let bundleResult = null; // Store bundle result for currency

    // Calculate one-time payments
    const oneTimePayments = [];
    
    // Build products array for bundle API (same logic as CalculatorContext)
    for (const product of selectedProducts) {
      if (!product.active) continue;
      
      switch (product.id) {
        case 'business-phone':
          // Prepare variables for Business Phone / FMC logic
          const isUkraineRegion = region === 'ua' ||
                                 String(region).toLowerCase().includes('ukr') ||
                                 String(region).toLowerCase() === 'ukraine';
          const businessPhoneType = settings.businessPhoneType || 'business-phone';
          // Apply minimum users based on tier: Corporate/Advanced requires minimum 10 users
          const minBpUsers = (packageTier === 'corporate' || packageTier === 'advanced') ? 10 : 3;
          const businessPhoneUsers = Math.max(products.businessPhoneUsers || 3, minBpUsers);
          let fmcUsers = settings.fmcUsers !== undefined ? settings.fmcUsers : (tabData.fmcUsers || 0);
          if (!fmcUsers) {
            fmcUsers = products.businessPhoneUsers || 0;
          }

          // Business Phone (only add if NOT standalone FMC)
          if (businessPhoneType !== 'fmc') {
            bundleProducts.push({
              product_id: 'business_phone',
              region,
              quantity: businessPhoneUsers,
              business_phone_type: businessPhoneType,
              package_tier: packageTier // Pass tier for proper pricing
            });

            // FMC add-on for business-phone-fmc (Ukraine only)
            if (businessPhoneType === 'business-phone-fmc' && fmcUsers > 0 && isUkraineRegion) {
              bundleProducts.push({
                product_id: 'fmc',
                region,
                quantity: fmcUsers, // No minimum for combined
                business_phone_type: businessPhoneType,
                package_tier: packageTier // Pass tier for proper pricing
              });
            }
          }

          // Standalone FMC (only for Ukraine region)
          if (businessPhoneType === 'fmc' && fmcUsers > 0 && isUkraineRegion) {
            bundleProducts.push({
              product_id: 'fmc',
              region,
              quantity: Math.max(fmcUsers, 6), // Minimum 6 for standalone
              business_phone_type: businessPhoneType,
              package_tier: packageTier // Pass tier for proper pricing
            });
          }
          break;
          
        case 'call-tracking':
          const callTrackingSessions = products.callTrackingSessions || 100;
          const callTrackingType = settings.callTrackingType || 'dynamic';
          const staticSources = settings.staticSources || 0;
          const callMeButtonEnabled = settings.callMeButtonEnabled || false;
          const numberFormatsQuantity = settings.numberFormatsQuantity || 1;
          
          // QSLN calculation function (replicated from CalculatorContext)
          const calculateQSLN = (traf) => {
            if (traf <= 0) return 0;
            
            // Convert traffic to coefficient: |traf| / 10 * 0.0833
            const traf_koef = Math.abs(traf) / 10 * 0.0833;
            
            if (traf_koef === 0 || isNaN(traf_koef)) return 0;
            
            let p0 = 1;
            let plast = 1;
            
            for (let i = 1; i < 10000; i++) {
              plast = plast * traf_koef / i;
              p0 = p0 + plast;
              
              if (i > 10000) break;
              
              // Check if convergence criterion is met
              if ((plast / p0) < 0.05) {
                const nums = (i > traf_koef * 1.05) ? i : Math.ceil(traf_koef * 1.05);
                return nums;
              }
            }
            
            return Math.ceil(traf_koef * 1.05);
          };

          // NEW: Call Me Button formula (same as CalculatorContext)
          const getCalltrackingNumbersQtyWithCallMe = (dailyTraf, numberFormatsQty) => {
            const numberOfSimultaneouslyCalls = (dailyTraf / 24 * 10) / 12 * 0.01;
            const result = numberOfSimultaneouslyCalls * 1.5;
            
            if (result > 5) {
              return Math.round(result) * numberFormatsQty;
            } else {
              return 5 * numberFormatsQty;
            }
          };
          
          // Calculate PRICING SLOTS (for backend pricing calculation - no Call Me reduction)
          let pricingSlots = 0;
          if (callTrackingType === 'static') {
            pricingSlots = staticSources * numberFormatsQuantity;
          } else if (callTrackingType === 'combined') {
            const dynamicSlots = calculateQSLN(callTrackingSessions || 0) * numberFormatsQuantity;
            pricingSlots = staticSources + dynamicSlots;
          } else {
            // Dynamic only (using QSLN)
            pricingSlots = calculateQSLN(callTrackingSessions || 0) * numberFormatsQuantity;
          }

          // Calculate ACTUAL PHONE NUMBERS (for UI display - with Call Me reduction)
          let actualPhoneNumbers = 0;
          if (callTrackingType === 'static') {
            // Static: Call Me doesn't apply, just return static sources * formats
            actualPhoneNumbers = staticSources * numberFormatsQuantity;
          } else if (callTrackingType === 'combined') {
            // Combined: static sources + Call Me applied to dynamic part
            let dynamicNumbers = 0;
            if (callMeButtonEnabled) {
              // Use new Call Me formula for dynamic part
              dynamicNumbers = getCalltrackingNumbersQtyWithCallMe(callTrackingSessions || 0, numberFormatsQuantity);
            } else {
              // No Call Me: apply formats to dynamic slots
              dynamicNumbers = calculateQSLN(callTrackingSessions || 0) * numberFormatsQuantity;
            }
            
            actualPhoneNumbers = staticSources + dynamicNumbers;
          } else {
            // Dynamic only
            if (callMeButtonEnabled) {
              // Use new Call Me formula
              actualPhoneNumbers = getCalltrackingNumbersQtyWithCallMe(callTrackingSessions || 0, numberFormatsQuantity);
            } else {
              // No Call Me: apply formats to dynamic slots
              actualPhoneNumbers = calculateQSLN(callTrackingSessions || 0) * numberFormatsQuantity;
            }
          }
          
          bundleProducts.push({
            product_id: 'call_tracking',
            region,
            quantity: pricingSlots, // Pricing slots (for backend pricing calculation)
            actual_phone_numbers: actualPhoneNumbers, // Actual numbers needed (for UI display)
            call_tracking_type: callTrackingType,
            static_sources: staticSources,
            call_me_button: callMeButtonEnabled,
            number_formats_quantity: numberFormatsQuantity
          });
          break;
          
        case 'ai-conversation':
          const aiCalls = products.aiCalls || 5000;
          // const nonEnglishAnalysis = settings.nonEnglishAnalysis || false; // Commented out - unified pricing now

          // For Basic/Advanced tier: AI uses users count (same as BP/FMC users)
          // For non-Basic or standalone AI: use aiCalls (minutes)
          let aiQuantity = aiCalls;
          if (packageTier === 'basic' || packageTier === 'advanced') {
            const fUsers = settings.fmcUsers !== undefined ? settings.fmcUsers : (products.businessPhoneUsers || 0);
            const businessPhoneType = settings.businessPhoneType || 'business-phone';
            aiQuantity = getEffectiveTierUsers({
              businessPhoneUsers: products.businessPhoneUsers,
              fmcUsers: fUsers,
              businessPhoneType,
              packageTier,
            });
          }

          bundleProducts.push({
            product_id: 'ai_conversation',
            region,
            quantity: aiQuantity,
            package_tier: packageTier // Pass tier for Pro/Corporate included products
            // non_english_ai: nonEnglishAnalysis // Commented out - unified pricing now
          });
          break;
          
        case 'callback':
          const callbackSessions = products.callbackSessions || 0;

          bundleProducts.push({
            product_id: 'callback',
            region,
            quantity: callbackSessions,
            package_tier: packageTier // Pass tier for Pro/Corporate included products
          });
          break;
          
        case 'voice-traffic':
          const voiceTrafficUseCustomAmount = settings.voiceTrafficUseCustomAmount || false;
          const voiceTrafficCustomAmount = settings.voiceTrafficCustomAmount || 0;
          const voiceTrafficCountriesMinutes = settings.voiceTrafficCountriesMinutes || { 'UA': 500 };
          
          // Deposit: send only if amount > 0
          if (voiceTrafficUseCustomAmount && voiceTrafficCustomAmount > 0) {
            bundleProducts.push({
              product_id: 'voice_traffic',
              region,
              use_custom_amount: true,
              custom_amount: voiceTrafficCustomAmount
            });
          }

          // Packages: sanitize and send if at least one country has minutes > 0
          const sanitizedCountriesMinutes = Object.fromEntries(
            Object.entries(voiceTrafficCountriesMinutes).map(([code, mins]) => [
              code,
              typeof mins === 'string' ? (parseInt(mins, 10) || 0) : (Number.isFinite(mins) ? mins : 0)
            ])
          );

          const hasValidCountries = Object.values(sanitizedCountriesMinutes).some(mins => mins > 0);
          // Avoid pushing EU default packages when deposit-only default is active
          const allowPackages = (!voiceTrafficUseCustomAmount) || (settings.hasVoiceTrafficCustomSettings === true);
          if (hasValidCountries && allowPackages) {
            bundleProducts.push({
              product_id: 'voice_traffic',
              region,
              use_custom_amount: false,
              voice_traffic_countries_minutes: sanitizedCountriesMinutes
            });
          }
          break;
          
        case 'omnichannel-chat':
          // Skip if FMC-only mode (no smartphone app = no chat)
          if ((settings.businessPhoneType || 'business-phone') === 'fmc') break;

          // For Basic/Advanced tier: Chat uses Business Phone users count (FMC excluded)
          // For non-Basic or standalone Chat: use omnichannelChatUsers
          let chatQuantity = Math.max(products.omnichannelChatUsers || 3, 3);
          if (packageTier === 'basic' || packageTier === 'advanced') {
            chatQuantity = getEffectiveChatUsers({
              businessPhoneUsers: products.businessPhoneUsers,
              businessPhoneType: settings.businessPhoneType || 'business-phone',
              packageTier,
            });
          }

          bundleProducts.push({
            product_id: 'omnichannel_chat',
            region,
            quantity: chatQuantity,
            package_tier: packageTier // Pass tier for Pro/Corporate included products
          });
          break;
          
        case 'eniq-ai': {
          const eniqAiMinutes = settings.eniqAiMinutes ?? tabData.eniqAiMinutes ?? 2000;
          const eniqAiPremiumModels = settings.eniqAiPremiumModels !== undefined ? settings.eniqAiPremiumModels : (tabData.eniqAiPremiumModels !== undefined ? tabData.eniqAiPremiumModels : true);
          const eniqTier = normalizeEniqTier(settings.eniqTier ?? tabData.eniqTier);

          // Per-user model: Eniq users follow Business Phone / FMC users,
          // but Eniq always bills at least ENIQ_MIN_BILLABLE_USERS (5) —
          // standalone AND anchored (BP basic/pro can carry only 3 users).
          const hasEniqUserBase = selectedProducts.some(
            p => (p.id === 'business-phone' || p.id === 'fmc') && p.active
          );
          const eniqFmcUsers = settings.fmcUsers !== undefined ? settings.fmcUsers : (products.businessPhoneUsers || 0);
          const eniqStandaloneUsers = settings.eniqStandaloneUsers ?? tabData.eniqStandaloneUsers ?? ENIQ_MIN_BILLABLE_USERS;
          const eniqUsers = hasEniqUserBase
            ? Math.max(
                getEffectiveTierUsers({
                  businessPhoneUsers: products.businessPhoneUsers,
                  fmcUsers: eniqFmcUsers,
                  businessPhoneType: settings.businessPhoneType || 'business-phone',
                  packageTier,
                }),
                ENIQ_MIN_BILLABLE_USERS
              )
            : Math.max(eniqStandaloneUsers, ENIQ_MIN_BILLABLE_USERS);

          bundleProducts.push({
            product_id: 'eniq_ai',
            region,
            quantity: isEniqPerUserMode() ? eniqUsers : eniqAiMinutes,
            package_tier: packageTier || '',
            eniq_tier: eniqTier,
            premium_models: eniqAiPremiumModels
          });
          break;
        }

        case 'voicebot':
          const voicebotMinutes = Math.max(settings.voicebotMinutes ?? tabData.voicebotMinutes ?? 1100, 1100);
          const voicebotAiMode = settings.voicebotAiMode !== undefined ? settings.voicebotAiMode : (tabData.voicebotAiMode !== undefined ? tabData.voicebotAiMode : true);
          const voicebotExtraChannels = settings.voicebotExtraChannels ?? tabData.voicebotExtraChannels ?? 0;

          bundleProducts.push({
            product_id: 'voicebot',
            region,
            quantity: voicebotMinutes,
            premium_models: voicebotAiMode,
            extra_channels: voicebotExtraChannels
          });
          break;

        case 'phone-numbers':
          // Phone numbers are calculated manually on frontend (NO bundle discounts).
          // Catalog prices come in mixed currencies and are stored USD-internal.
          // Per-region normalization keeps the inactive-tab snapshot total in
          // the same currency as backend products contribute (EUR for EU).
          if (settings.phoneNumbers && Array.isArray(settings.phoneNumbers)) {
            const phoneItems = buildPhoneNumberPricingItems({
              phoneNumbers: settings.phoneNumbers,
              t,
            });
            for (const item of phoneItems) {
              totalUSD += normalizePhoneLineForTotal(item.price, region, currencyRates);
              baseSummaryItems.push(item);
              finalSummaryItems.push(item);
            }

            // Calculate activation fees if enabled (kept separate — utility
            // only owns summary-line shape, not one-time payment accounting).
            if (settings.enablePhoneActivation) {
              for (const phone of settings.phoneNumbers) {
                if (phone.quantity > 0 && phone.activationFee) {
                  const activationFee = phone.quantity * (phone.activationFee || 0);
                  const description = t('calculator.phoneNumberActivation', 'Phone numbers activation');
                  const existingPhoneActivation = oneTimePayments.find(p => p.description === description);
                  if (existingPhoneActivation) {
                    existingPhoneActivation.total += activationFee;
                  } else {
                    oneTimePayments.push({
                      description,
                      total: activationFee,
                      hasActivation: true
                    });
                  }
                }
              }
            }
          }
          break;
        default:
          break;
      }
    }

    // For Pro/Corporate tiers: auto-add included products if not already added.
    // Pro includes AI Conversation; Corporate includes Eniq AI Standard instead.
    // Both include Callback and Chat. All are added with a $0 price.
    const hasBusinessPhone = bundleProducts.some(p => p.product_id === 'business_phone' || p.product_id === 'fmc');
    if (hasBusinessPhone && (packageTier === 'pro' || packageTier === 'corporate')) {
      // Get effective users for included products (same as BP/FMC users)
      const fUsers = settings.fmcUsers !== undefined ? settings.fmcUsers : (products.businessPhoneUsers || 0);
      const businessPhoneType = settings.businessPhoneType || 'business-phone';
      const effectiveUsers = getEffectiveTierUsers({
        businessPhoneUsers: products.businessPhoneUsers,
        fmcUsers: fUsers,
        businessPhoneType,
        packageTier,
      });

      // Add AI Conversation if not already added — Pro only
      const hasAI = bundleProducts.some(p => p.product_id === 'ai_conversation');
      if (!hasAI && packageTier === 'pro') {
        bundleProducts.push({
          product_id: 'ai_conversation',
          region,
          quantity: effectiveUsers,
          package_tier: packageTier
        });
      }

      // Add Eniq AI Standard if not already added — Corporate only.
      // Eniq always bills at least ENIQ_MIN_BILLABLE_USERS (5).
      const hasEniq = bundleProducts.some(p => p.product_id === 'eniq_ai');
      if (!hasEniq && packageTier === 'corporate') {
        bundleProducts.push({
          product_id: 'eniq_ai',
          region,
          quantity: Math.max(effectiveUsers, ENIQ_MIN_BILLABLE_USERS),
          package_tier: packageTier,
          eniq_tier: 'standard'
        });
      }

      // Add Callback if not already added
      const hasCallback = bundleProducts.some(p => p.product_id === 'callback');
      if (!hasCallback) {
        // For Pro/Corporate: Callback sessions = effectiveUsers * 100 (default)
        const callbackSessions = effectiveUsers * 100;
        bundleProducts.push({
          product_id: 'callback',
          region,
          quantity: callbackSessions,
          package_tier: packageTier
        });
      }

      // Add Omnichannel Chat if not already added — skip for FMC-only (no smartphone app)
      const hasChat = bundleProducts.some(p => p.product_id === 'omnichannel_chat');
      if (!hasChat && businessPhoneType !== 'fmc') {
        bundleProducts.push({
          product_id: 'omnichannel_chat',
          region,
          // Chat counts Business Phone users only — FMC users cannot use chat
          quantity: getEffectiveChatUsers({
            businessPhoneUsers: products.businessPhoneUsers,
            businessPhoneType,
            packageTier,
          }),
          package_tier: packageTier
        });
      }
    }

    // For Advanced tier: auto-add only Callback (included at $0). AI and Chat are paid add-ons.
    if (hasBusinessPhone && packageTier === 'advanced') {
      const fUsers = settings.fmcUsers !== undefined ? settings.fmcUsers : (products.businessPhoneUsers || 0);
      const businessPhoneType = settings.businessPhoneType || 'business-phone';
      const effectiveUsers = getEffectiveTierUsers({
        businessPhoneUsers: products.businessPhoneUsers,
        fmcUsers: fUsers,
        businessPhoneType,
        packageTier,
      });

      const hasCallback = bundleProducts.some(p => p.product_id === 'callback');
      if (!hasCallback) {
        const callbackSessions = effectiveUsers * 100;
        bundleProducts.push({
          product_id: 'callback',
          region,
          quantity: callbackSessions,
          package_tier: packageTier
        });
      }
    }

    // Calculate bundle pricing if we have products
    if (bundleProducts.length > 0) {
      try {
        const bundleRequest = {
          region,
          products: bundleProducts,
          very_custom_product_discounts: settings.veryCustomProductDiscounts || {}
        };
        
        bundleResult = await calculateBundle(bundleRequest);
        
        // Get bundle total
        totalUSD += bundleResult.total.primary.amount;
        
        // Process bundle products into summary items
        bundleResult.products.forEach(product => {
          const productId = product.product_id;
          const basePrice = product.base_price;
          const finalPrice = product.final_price; // Price with bundle discounts
          
          // Create summary items based on product type (using base prices for subscription calculator)
          if (productId === 'business_phone') {
            // Apply minimum users based on tier: Corporate requires minimum 10 users
            const minUsers = getMinUsersForTier(packageTier);
            const users = Math.max(products.businessPhoneUsers || 3, minUsers);
            // Format tier label with capitalized first letter
            const tierLabel = packageTier ? packageTier.charAt(0).toUpperCase() + packageTier.slice(1) : '';
            const productLabel = `Business Phone ${tierLabel}`.trim();
            const item = {
              label: `${productLabel} (${users} ${t('calculator.businessPhoneUsers', 'users')})`,
              productType: 'business-phone',
              users: users,
              tier: packageTier // Package tier: basic, pro, corporate
            };
            baseSummaryItems.push({ ...item, price: basePrice });
            finalSummaryItems.push({ ...item, price: finalPrice });
          } else if (productId === 'fmc') {
            const businessPhoneType = settings.businessPhoneType || 'business-phone';
            let fmcUsersVal = settings.fmcUsers !== undefined ? settings.fmcUsers : (tabData.fmcUsers || 0);
            if (!fmcUsersVal) {
              fmcUsersVal = products.businessPhoneUsers || 0;
            }
            const users = businessPhoneType === 'fmc' ? Math.max(fmcUsersVal, 6) : fmcUsersVal;
            // Format tier label with capitalized first letter
            const tierLabel = packageTier ? packageTier.charAt(0).toUpperCase() + packageTier.slice(1) : '';
            const productLabel = `FMC ${tierLabel}`.trim();
            const item = {
              label: `${productLabel} (${users} ${t('calculator.fmcUsers', 'users')})`,
              productType: 'fmc',
              users: users,
              tier: packageTier // Package tier: basic, pro, corporate
            };
            baseSummaryItems.push({ ...item, price: basePrice });
            finalSummaryItems.push({ ...item, price: finalPrice });
          } else if (productId === 'call_tracking') {
            const callTrackingSessions = products.callTrackingSessions || 100;
            const callTrackingType = settings.callTrackingType || 'dynamic';
            const staticSources = settings.staticSources || 0;
            const callMeButtonEnabled = settings.callMeButtonEnabled || false;
            const numberFormatsQuantity = settings.numberFormatsQuantity || 1;

            // IMPORTANT: Use actual phone numbers for DISPLAY, not pricing slots!
            // This is already calculated in the first Call Tracking block above
            // We need to re-calculate it here for consistency
            const calculateQSLN = (traf) => {
              if (traf <= 0) return 0;
              const traf_koef = Math.abs(traf) / 10 * 0.0833;
              if (traf_koef === 0 || isNaN(traf_koef)) return 0;
              
              let p0 = 1;
              let plast = 1;
              
              for (let i = 1; i < 10000; i++) {
                plast = plast * traf_koef / i;
                p0 = p0 + plast;
                
                if (i > 10000) break;
                
                if ((plast / p0) < 0.05) {
                  const nums = (i > traf_koef * 1.05) ? i : Math.ceil(traf_koef * 1.05);
                  return nums;
                }
              }
              
              return Math.ceil(traf_koef * 1.05);
            };

            const getCalltrackingNumbersQtyWithCallMe = (dailyTraf, numberFormatsQty) => {
              const numberOfSimultaneouslyCalls = (dailyTraf / 24 * 10) / 12 * 0.01;
              const result = numberOfSimultaneouslyCalls * 1.5;
              
              if (result > 5) {
                return Math.round(result) * numberFormatsQty;
              } else {
                return 5 * numberFormatsQty;
              }
            };

            // Calculate actual phone numbers for display (same logic as above)
            let actualNumbers = 0;
            if (callTrackingType === 'static') {
              actualNumbers = staticSources * numberFormatsQuantity;
            } else if (callTrackingType === 'combined') {
              let dynamicNumbers = 0;
              if (callMeButtonEnabled) {
                dynamicNumbers = getCalltrackingNumbersQtyWithCallMe(callTrackingSessions || 0, numberFormatsQuantity);
              } else {
                dynamicNumbers = calculateQSLN(callTrackingSessions || 0) * numberFormatsQuantity;
              }
              actualNumbers = staticSources + dynamicNumbers;
            } else {
              if (callMeButtonEnabled) {
                actualNumbers = getCalltrackingNumbersQtyWithCallMe(callTrackingSessions || 0, numberFormatsQuantity);
              } else {
                actualNumbers = calculateQSLN(callTrackingSessions || 0) * numberFormatsQuantity;
              }
            }

            const item = {
              label: `Call Tracking (${actualNumbers} ${t('calculator.callTrackingSlots', 'slots')})`,
              productType: 'call-tracking',
              slots: actualNumbers, // Use actual numbers for display
              sessions: callTrackingSessions
            };
            baseSummaryItems.push({ ...item, price: basePrice });
            finalSummaryItems.push({ ...item, price: finalPrice });
          } else if (productId === 'ai_conversation') {
            const aiCalls = products.aiCalls || 5000;

            // For Basic/Advanced tier: use users count (same as BP/FMC users)
            const isBasicTier = packageTier === 'basic' || packageTier === 'advanced';
            let effectiveAiUsers = 0;
            if (isBasicTier) {
              const fUsers = settings.fmcUsers !== undefined ? settings.fmcUsers : (products.businessPhoneUsers || 0);
              const businessPhoneType = settings.businessPhoneType || 'business-phone';
              effectiveAiUsers = getEffectiveTierUsers({
                businessPhoneUsers: products.businessPhoneUsers,
                fmcUsers: fUsers,
                businessPhoneType,
                packageTier,
              });
            }

            const aiLabel = isBasicTier
              ? `${t('calculator.aiConversationLabel', 'AI Conversation Intelligence')} (${effectiveAiUsers} ${t('calculator.businessPhoneUsers', 'users')})`
              : `${t('calculator.aiConversationLabel', 'AI Conversation Intelligence')} (${aiCalls} ${t('calculator.minutes', 'minutes')})`;

            const item = {
              label: aiLabel,
              productType: 'ai-conversation',
              users: isBasicTier ? effectiveAiUsers : undefined,
              minutes: !isBasicTier ? aiCalls : undefined,
              calls: aiCalls
            };
            baseSummaryItems.push({ ...item, price: basePrice });
            finalSummaryItems.push({ ...item, price: finalPrice });
          } else if (productId === 'callback') {
            const callbackSessions = products.callbackSessions || 0;
            const item = {
              label: `Callback Widget (${callbackSessions} ${t('calculator.callbackSessions', 'sessions')})`,
              productType: 'callback',
              sessions: callbackSessions
            };
            baseSummaryItems.push({ ...item, price: basePrice });
            finalSummaryItems.push({ ...item, price: finalPrice });
          } else if (productId === 'voice_traffic') {
            // Use bundle product flag to distinguish deposit vs packages
            if (product.use_custom_amount) {
              const amount = product.custom_amount || settings.voiceTrafficCustomAmount || 0;
              const item = {
                label: `${t('calculator.voiceTrafficCustomAmount', 'Deposit for outbound calls')} (${amount})`,
                productType: 'voice-traffic',
                useCustomAmount: true,
                customAmount: amount,
                minutes: 0
              };
              baseSummaryItems.push({ ...item, price: basePrice });
              finalSummaryItems.push({ ...item, price: finalPrice });
            } else {
              // Package mode - get country data from bundle response
              if (product.voice_traffic_countries && typeof product.voice_traffic_countries === 'object') {
                // Multiple countries with detailed package info
                Object.entries(product.voice_traffic_countries).forEach(([countryCode, countryData]) => {
                  // Bundle API now provides all overage data directly - no calculation needed!
                  
                  // Create structured data for formatVoiceTrafficLabel function using Bundle API data
                  const packageData = {
                    countryCode: countryCode,
                    minutes: countryData.minutes || 0,
                    priceLocal: countryData.price_per_minute_local || countryData.price_local || 0,
                    currency: countryData.currency || 'USD',
                    packageName: countryData.package_name, // Fallback for legacy parsing
                    overageRate: countryData.overage_rate_local || 0
                  };
                  
                  // Get requested minutes from Bundle API
                  const requestedMinutes = countryData.requested_minutes || countryData.minutes || 0;
                  
                  // Generate consistent labels using formatVoiceTrafficLabel
                  const titleLabel = formatVoiceTrafficLabel(packageData, 'title', t, { requestedMinutes });
                  
                  // Create metadata with overage using Bundle API data
                  const baseMinutes = countryData.minutes || 0;
                  const overageMinutes = countryData.overage_minutes || 0;
                  let metadataLabel = `${baseMinutes} ${t('calculator.voiceTrafficMinutes', 'хвилин')}`;
                  if (overageMinutes > 0) {
                    metadataLabel += ` + ${overageMinutes} ${t('calculator.voiceTrafficOverageText', 'поза пакетом')}`;
                  }
                  
                  
                  const item = {
                    label: titleLabel, // Use title format for product name: "Пакет хвилин Україна"
                    metadata: metadataLabel, // Volume info with overage: "10000 хвилин + 1100 поза пакетом"
                    productType: 'voice-traffic',
                    useCustomAmount: false,
                    customAmount: 0,
                    minutes: countryData.minutes || 0,
                    countryCode: countryCode,
                    packageName: countryData.package_name || '',
                    packageId: countryData.package_id || '',
                    priceLocal: countryData.price_local || 0,
                    currency: countryData.currency || 'USD',
                    countries: [countryCode],
                    // NEW: Use Bundle API data directly
                    requestedMinutes: countryData.requested_minutes || 0,
                    overageMinutes: countryData.overage_minutes || 0,
                    pricePerMinuteLocal: countryData.price_per_minute_local || 0,
                    pricePerMinuteUSD: countryData.price_per_minute_usd || 0,
                    overageRateLocal: countryData.overage_rate_local || 0,
                    overageRateUSD: countryData.overage_rate_usd || 0,
                    overageCostUSD: countryData.overage_cost_usd || 0
                  };
                  baseSummaryItems.push({ ...item, price: countryData.price_usd || 0 });
                  finalSummaryItems.push({ ...item, price: countryData.total_cost_usd || countryData.price_usd || 0 });
                });
              }
            }
          } else if (productId === 'omnichannel_chat') {
            // Skip if FMC-only mode (no smartphone app = no chat)
            if ((settings.businessPhoneType || 'business-phone') === 'fmc') return;

            // For Basic/Advanced tier: Chat uses Business Phone users count (FMC excluded)
            const isBasicTierChat = packageTier === 'basic' || packageTier === 'advanced';
            let effectiveChatUsers = Math.max(products.omnichannelChatUsers || 3, 3);
            if (isBasicTierChat) {
              effectiveChatUsers = getEffectiveChatUsers({
                businessPhoneUsers: products.businessPhoneUsers,
                businessPhoneType: settings.businessPhoneType || 'business-phone',
                packageTier,
              });
            }

            const item = {
              label: `${t('calculator.omnichannelChat', 'Omnichannel Chat')} (${effectiveChatUsers} ${t('calculator.chatUsers', 'users')})`,
              productType: 'omnichannel-chat',
              users: effectiveChatUsers
            };
            baseSummaryItems.push({ ...item, price: basePrice });
            finalSummaryItems.push({ ...item, price: finalPrice });
          } else if (productId === 'eniq_ai' && isEniqPerUserMode()) {
            const eniqUsers = product.users || 0;
            const resolvedEniqTier = normalizeEniqTier(product.eniq_tier);
            // Local fallback needs a phone anchor: without Business Phone/FMC in
            // the bundle the backend prices Eniq standalone, so the tier alone
            // must not render the "included" label (stale saved tabs).
            const eniqIncluded = product.included_in_tier || (hasBusinessPhone && isEniqIncludedInTier(resolvedEniqTier, packageTier));
            const eniqTierName = getEniqTierDisplayName(resolvedEniqTier);

            const item = {
              label: eniqIncluded
                ? `${t('calculator.eniqAiLabel', 'Eniq AI')} ${eniqTierName}`
                : `${t('calculator.eniqAiLabel', 'Eniq AI')} ${eniqTierName} (${eniqUsers} ${t('calculator.businessPhoneUsers', 'users')})`,
              productType: 'eniq-ai',
              eniqTier: resolvedEniqTier,
              users: eniqUsers,
              price_per_user: product.price_per_user || 0
            };
            baseSummaryItems.push({ ...item, price: basePrice });
            finalSummaryItems.push({ ...item, price: finalPrice });
          } else if (productId === 'eniq_ai') {
            // Legacy per-minute model (conserved)
            // Use ALL data from Bundle API - no frontend generation
            const selectedPlan = product.selected_plan || 'start';
            const includedMinutes = product.included_minutes || 0;
            const extraMinutes = product.extra_minutes || 0;
            const totalMinutes = product.total_minutes || 0;
            const premiumModels = product.premium_models !== undefined ? product.premium_models : true;
            const includedUnits = product.included_units || 0;
            const extraUnits = product.extra_units || 0;
            const totalUnits = product.total_units || 0;
            const pricePerUnit = product.price_per_unit || 0;
            const extraCost = product.extra_cost || 0;
            
            const planName = getPlanName(selectedPlan, t);
            const item = {
              label: `${t('calculator.eniqAiLabel', 'Eniq AI')} (${Math.floor(totalUnits).toLocaleString()} ${t('calculator.eniqAiUnits', 'units')} ${planName})`,
              productType: 'eniq-ai',
              minutes: totalMinutes, // Use backend data
              premium_models: premiumModels, // Use backend data
              selected_plan: selectedPlan,
              included_minutes: includedMinutes,
              extra_minutes: extraMinutes,
              total_minutes: totalMinutes,
              included_units: includedUnits,
              extra_units: extraUnits,
              total_units: totalUnits,
              price_per_unit: pricePerUnit,
              extra_cost: extraCost
            };
            baseSummaryItems.push({ ...item, price: basePrice });
            finalSummaryItems.push({ ...item, price: finalPrice });
          } else if (productId === 'voicebot') {
            // Use ALL data from Bundle API
            const selectedPlan = product.selected_plan || 'lite';
            const includedMinutes = product.included_minutes || 0;
            const extraMinutes = product.extra_minutes || 0;
            const totalMinutes = product.total_minutes || 0;
            const premiumModels = product.premium_models !== undefined ? product.premium_models : true;
            const includedUnits = product.included_units || 0;
            const extraUnits = product.extra_units || 0;
            const totalUnits = product.total_units || 0;
            const pricePerUnit = product.price_per_unit || 0;
            const extraCost = product.extra_cost || 0;

            const planName = getPlanName(selectedPlan, t);
            const item = {
              label: `${t('calculator.voicebotLabel', 'VoiceBot')} (${Math.floor(totalUnits).toLocaleString()} ${t('calculator.voicebotMinutesUnit', 'min')} ${planName})`,
              productType: 'voicebot',
              minutes: totalMinutes,
              premium_models: premiumModels,
              selected_plan: selectedPlan,
              included_minutes: includedMinutes,
              extra_minutes: extraMinutes,
              total_minutes: totalMinutes,
              included_units: includedUnits,
              extra_units: extraUnits,
              total_units: totalUnits,
              price_per_unit: pricePerUnit,
              extra_cost: extraCost
            };
            baseSummaryItems.push({ ...item, price: basePrice });
            finalSummaryItems.push({ ...item, price: finalPrice });
          }
        });

        // NOTE: bundle contribution was already added to totalUSD at line ~621
        // (`totalUSD += bundleResult.total.primary.amount`) before the products
        // loop ran. Re-assigning here would clobber the phone-numbers
        // contribution accumulated earlier in the selectedProducts loop.
      } catch (error) {
        console.error('[usePricingSnapshot] Failed to calculate bundle pricing for inactive tab:', error);
        throw error;
      }
    }

    // Normalize all items to region pricing currency so OfferPage,
    // ComparisonPage and ProductDisplay can format from a uniform contract.
    const normalize = (item) => normalizePricingItemForRegion(item, region, currencyRates);
    for (let i = 0; i < baseSummaryItems.length; i++) baseSummaryItems[i] = normalize(baseSummaryItems[i]);
    for (let i = 0; i < finalSummaryItems.length; i++) finalSummaryItems[i] = normalize(finalSummaryItems[i]);

    // Add Eniq AI implementation fee if enabled
    if (settings.enableEniqImplementationFee && selectedProducts.some(p => p.id === 'eniq-ai' && p.active) && settings.eniqAiImplementationFee > 0) {
      oneTimePayments.push({
        description: t('calculator.eniqImplementationFee', 'Eniq AI Implementation'),
        total: settings.eniqAiImplementationFee,
        hasActivation: true
      });
    }

    // Add VoiceBot implementation fee if enabled
    if (settings.enableVoicebotImplementationFee && selectedProducts.some(p => p.id === 'voicebot' && p.active) && settings.voicebotImplementationFee > 0) {
      oneTimePayments.push({
        description: t('calculator.voicebotImplementationFeeLabel', 'VoiceBot Implementation'),
        total: settings.voicebotImplementationFee,
        hasActivation: true
      });
    }

    // Calculate total one-time payments
    const totalOneTimePayments = oneTimePayments.reduce(
      (sum, payment) => sum + convertUSDToSubscriptionAmount(payment.total, region, currencyRates),
      0
    );

    // Calculate subscription plans using bundle total as baseline
    const subscriptionSettings = settings.subscriptionSettings || {};
    const subscriptionOptions = {
      show360Days: true,
      show180Days: subscriptionSettings.subscription180Days || false,
      show90Days: subscriptionSettings.subscription90Days || false,
      show12Month: (subscriptionSettings.subscription12Month || false) && totalUSD >= 250,
      // Same rule as the active-tab path: UA-only and respects an explicit
      // user opt-out from the subscription settings dialog.
      showSplitPayment: region === 'ua' && subscriptionSettings.subscriptionSplitPayment !== false
    };

    // Get custom discounts from tab data or use defaults
    const tabCustomDiscount360 = settings.customDiscount360 ?? 20;
    const tabCustomDiscount180 = settings.customDiscount180 ?? 0;

    const subscriptionData = calculateSubscriptionPlans(
      baseSummaryItems,
      subscriptionOptions,
      region,
      totalUSD, // Pass bundle total as baseline for 360-day plan
      finalSummaryItems, // Pass bundle-discounted products for proper subscription discount calculation
      tabCustomDiscount360, // Pass custom 360-day discount percentage
      tabCustomDiscount180  // Pass custom 180-day discount percentage
    );

    // Add one-time payments to subscription payment amounts
    const plansWithOneTimePayments = subscriptionData.plans.map(plan => ({
      ...plan,
      paymentAmount: plan.paymentAmount + totalOneTimePayments
    }));
    
    // Apply 360-day subscription discount to eligible products for individual_prices (exclude phone-numbers)
    const individual_prices = sortProductsByPriority(finalSummaryItems.map(item => {
      const productId = item.productType;
      const isEligibleForDiscount = SUBSCRIPTION_ELIGIBLE_PRODUCT_TYPES.includes(productId);
      const discountFactor = 1 + (tabCustomDiscount360 / 100);
      const discountedPrice = isEligibleForDiscount ? item.price / discountFactor : item.price; // Custom 360-day subscription discount
      
      return {
        ...item,
        price: discountedPrice,
        // Include all metadata for UI display
        users: item.users,
        sessions: item.sessions,
        slots: item.slots,
        calls: item.calls,
        minutes: item.minutes,
        count: item.count,
        name: item.name,
        tier: item.tier, // Package tier: basic, pro, corporate
        // Voice Traffic specific fields
        countries: item.countries,
        countryCode: item.countryCode,
        packageName: item.packageName,
        packageId: item.packageId,
        priceLocal: item.priceLocal,
        currency: item.currency,
        useCustomAmount: item.useCustomAmount,
        customAmount: item.customAmount,
        detailedLabel: item.detailedLabel,
        requestedMinutes: item.requestedMinutes,
        overageMinutes: item.overageMinutes,
        overageRate: item.overageRate,
        // IMPORTANT: Include metadata for voice traffic overage display
        metadata: item.metadata,
        // Eniq AI specific fields
        selected_plan: item.selected_plan,
        eniqTier: item.eniqTier,
        included_minutes: item.included_minutes,
        extra_minutes: item.extra_minutes,
        total_minutes: item.total_minutes,
        premium_models: item.premium_models,
        included_units: item.included_units,
        extra_units: item.extra_units,
        total_units: item.total_units,
        price_per_unit: item.price_per_unit,
        extra_cost: item.extra_cost
      };
    }));

    return {
      individual_prices,
      subscription_plans: plansWithOneTimePayments,
      total_monthly: subscriptionData.defaultPlan?.monthlyPrice || totalUSD,
      one_time_payments: oneTimePayments,
      currency: bundleResult?.total?.primary?.symbol || getCurrencySymbolForRegion(region),
      region: region,
      currencyRates: currencyRates,
      custom_discount_360: tabCustomDiscount360, // Include custom discounts
      custom_discount_180: tabCustomDiscount180,
      calculated_at: new Date().toISOString()
    };
  }, [calculateBundle, region, t, currencyRates]);

  return {
    generateSnapshotForActiveTab,
    generateSnapshotForInactiveTab
  };
};

export default usePricingSnapshot;
